"use client";

import { useEffect, useMemo, useState } from "react";
import { createClient } from "@/lib/supabase/client";

/**
 * Plan de l'utilisateur courant, côté client, pour masquer les modules
 * auxquels il n'a pas accès dans les menus de navigation.
 *
 * La RLS « Users can check own whitelist status » autorise la lecture de
 * SA propre ligne `allowed_users` (match email) → on peut lire le plan.
 * L'enforcement réel reste côté serveur (proxy/middleware) ; ici c'est
 * uniquement pour l'affichage.
 *
 * Cache module-level partagé entre tous les composants + REVALIDATION :
 * TTL court et refetch au retour sur l'app (focus / visibilitychange).
 * Sans ça, une PWA qui reste ouverte des jours gardait indéfiniment les
 * droits lus au premier chargement — les changements d'accès faits par
 * l'admin n'arrivaient jamais jusqu'aux menus.
 */

import { modulesAccessibles, modulesDuPlan, TOUS_LES_MODULES, type ModuleApp } from "./modules-plan";

export type AppModule = ModuleApp;
export const ALL_MODULES: AppModule[] = [...TOUS_LES_MODULES];

interface PlanInfo { plan: string; modules: string[] | null }

const TTL_MS = 60_000;
let cached: PlanInfo | null = null;
let cachedAt = 0;
let inflight: Promise<PlanInfo> | null = null;
// Tous les hooks montés : notifiés quand le cache se rafraîchit.
const subscribers = new Set<(p: PlanInfo) => void>();

function fetchPlan(): Promise<PlanInfo> {
  if (cached && Date.now() - cachedAt < TTL_MS) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) {
        // Session pas (encore) hydratée : on NE met PAS en cache pour
        // re-tenter au prochain focus/mount — sinon un raté au boot
        // affichait tous les modules pour toute la vie de la page.
        return cached ?? { plan: "pro_gestic", modules: null };
      }
      const { data, error } = await supabase
        .from("allowed_users")
        .select("plan, modules")
        .eq("email", user.email.toLowerCase())
        .maybeSingle();
      // Erreur de lecture (RLS, réseau…) : on garde la dernière valeur
      // connue, sans caber le fallback (retry au prochain cycle).
      if (error) return cached ?? { plan: "pro_gestic", modules: null };
      // Plan absent (colonne manquante / null) = legacy → accès total.
      cached = {
        plan: (data?.plan as string | undefined) ?? "pro_gestic",
        modules: (data?.modules as string[] | null | undefined) ?? null,
      };
      cachedAt = Date.now();
      const snapshot = cached;
      subscribers.forEach((fn) => fn(snapshot));
      return cached;
    } catch {
      return cached ?? { plan: "pro_gestic", modules: null };
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** État partagé + revalidation au focus (PWA longue durée). */
function useSubscribedPlan(): PlanInfo {
  const [info, setInfo] = useState<PlanInfo>(cached ?? { plan: "pro_gestic", modules: null });
  useEffect(() => {
    let active = true;
    const update = (p: PlanInfo) => { if (active) setInfo(p); };
    subscribers.add(update);
    fetchPlan().then(update);
    const revalidate = () => { void fetchPlan(); };
    const onVis = () => { if (document.visibilityState === "visible") revalidate(); };
    window.addEventListener("focus", revalidate);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      active = false;
      subscribers.delete(update);
      window.removeEventListener("focus", revalidate);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);
  return info;
}

export function useUserPlan(): string {
  return useSubscribedPlan().plan;
}

/** Set des modules accessibles (combine plan + grants explicites par user). */
export function useAccessibleModules(): Set<AppModule> {
  const info = useSubscribedPlan();
  return useMemo(() => accessibleModules(info.plan, info.modules), [info]);
}

/** Modules visibles pour un plan (règle unique : lib/auth/modules-plan.ts). */
export function modulesForPlan(plan: string): Set<AppModule> {
  return modulesDuPlan(plan);
}

/** Modules accessibles : accès donnés à la main, sinon le plan. */
export function accessibleModules(plan: string, modules: string[] | null | undefined): Set<AppModule> {
  return modulesAccessibles(plan, modules);
}
